import React, { useState, useRef } from "react";
import { X, AlertCircle } from "lucide-react";

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * Chip-style tokenized domain input.
 *
 * Props:
 *   value: string[]  — controlled list of domains
 *   onChange: (list: string[]) => void
 *   placeholder?: string
 *   variant?: "trusted" | "blocked" | "neutral"
 *   max?: number (default 200)
 *   testIdPrefix?: string
 */
export default function DomainChipInput({
    value = [],
    onChange,
    placeholder = "es. partner-pec.it — invio o virgola per aggiungere",
    variant = "neutral",
    max = 200,
    testIdPrefix = "domain-input",
}) {
    const [buffer, setBuffer] = useState("");
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const commit = (raw) => {
        const items = raw.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
        if (items.length === 0) return;
        const errors = [];
        const additions = [];
        for (const d of items) {
            if (d.length > 253 || !DOMAIN_RE.test(d)) {
                errors.push(d);
                continue;
            }
            if (value.includes(d) || additions.includes(d)) continue;
            additions.push(d);
        }
        if (value.length + additions.length > max) {
            setError(`Massimo ${max} domini`);
            return;
        }
        if (errors.length > 0) {
            setError(`Non validi: ${errors.slice(0, 3).join(", ")}${errors.length > 3 ? "…" : ""}`);
        } else {
            setError("");
        }
        if (additions.length > 0) {
            onChange([...value, ...additions]);
        }
        setBuffer("");
    };

    const removeAt = (idx) => {
        const next = value.filter((_, i) => i !== idx);
        onChange(next);
    };

    const handleKey = (ev) => {
        if (ev.key === "Enter" || ev.key === "," || ev.key === ";") {
            ev.preventDefault();
            if (buffer.trim()) commit(buffer);
        } else if (ev.key === "Backspace" && !buffer && value.length > 0) {
            removeAt(value.length - 1);
        }
    };

    const chipClass = variant === "blocked"
        ? "bg-[#DC2626]/10 text-[#991B1B] border-[#DC2626]/30"
        : variant === "trusted"
            ? "bg-[#16A34A]/10 text-[#166534] border-[#16A34A]/30"
            : "bg-slate-100 text-slate-700 border-slate-200";

    return (
        <div>
            <div
                data-testid={`${testIdPrefix}-wrapper`}
                onClick={() => inputRef.current?.focus()}
                className="flex flex-wrap gap-2 min-h-[44px] border border-[#DCE3EE] bg-white rounded-lg p-2 focus-within:border-[#2563EB] transition-colors"
            >
                {value.map((d, i) => (
                    <span
                        key={`${d}-${i}`}
                        data-testid={`${testIdPrefix}-chip-${i}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${chipClass}`}
                    >
                        <span className="font-mono">{d}</span>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                            data-testid={`${testIdPrefix}-remove-${i}`}
                            className="ml-0.5 rounded-full hover:bg-black/10 p-0.5"
                            aria-label={`Rimuovi ${d}`}
                        >
                            <X className="w-3 h-3" strokeWidth={2.5} />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    data-testid={`${testIdPrefix}-input`}
                    type="text"
                    value={buffer}
                    onChange={(e) => { setBuffer(e.target.value); if (error) setError(""); }}
                    onKeyDown={handleKey}
                    onBlur={() => buffer.trim() && commit(buffer)}
                    placeholder={value.length === 0 ? placeholder : ""}
                    className="flex-1 min-w-[160px] bg-transparent outline-none text-sm text-[#172033]"
                />
            </div>
            <div className="flex items-center justify-between mt-1.5">
                <div className="text-[11px] text-[#64748B]">
                    {value.length}/{max} domini · Invio o virgola per aggiungere · Backspace per rimuovere l&apos;ultimo
                </div>
                {error && (
                    <div className="flex items-center gap-1 text-[11px] text-[#DC2626]" data-testid={`${testIdPrefix}-error`}>
                        <AlertCircle className="w-3 h-3" />
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
}
